import type { User } from "@auth0/auth0-react";
import posthog, { type CaptureResult, type Properties } from "posthog-js";

import { Backend, getBackend, getWieldyOrgId } from "../api/apiConfig";
import { PROD_DEMO_ORG_ID } from "./devmode";

const ENV_NAME = {
  [Backend.Local]: "local",
  [Backend.Staging]: "staging",
  [Backend.Demo]: "demo",
  [Backend.Production]: "production",
};

const MAX_SUPER_PRACTICE_IDS = 10;

// Publishable key, hardcoded per env like GTM ids; local opts in via .env.local.
const POSTHOG_KEY: Record<Backend, string | undefined> = {
  [Backend.Local]: import.meta.env.VITE_POSTHOG_KEY,
  [Backend.Staging]: "phc_ARHAyocShw5MGRCQgCJXvWP2rYswypvDJ8jFqNpoqgu3",
  [Backend.Demo]: undefined,
  [Backend.Production]: "phc_ARHAyocShw5MGRCQgCJXvWP2rYswypvDJ8jFqNpoqgu3",
};

interface PostHogConfig {
  analyticsEnabled: boolean;
  replayEnabled: boolean;
  replayBaselineSampleRate: number;
  replayExcludedOrgIds: string[];
}

const REPLAY_PHI_FIELDS = [
  "dob",
  "name",
  "notes",
  "patientDob",
  "patientFullName",
  "patientKey",
  "patientName",
  "subscriberId",
  "username",
  "wieldyPatientName",
  "wieldyPolicyNotes",
  "wieldySubscriberName",
];

const REPLAY_MASKED_SELECTOR = [
  "[data-sr-redact]",
  ".MuiDialogTitle-root",
  ...REPLAY_PHI_FIELDS.map((field) => `[data-field="${field}"]`),
].join(",");

let initialized = false;
let analyticsEnabled = true;
let replayAllowed: boolean | null = null;
let replayRecording = false;
let baselineRolled = false;
const pendingReplayBoosts = new Map<string, number>();

function isInternalOrg(orgId: string | undefined): boolean {
  return !orgId || [getWieldyOrgId(), PROD_DEMO_ORG_ID].includes(orgId);
}

// PostHog attaches $current_url/$pathname/$referrer to every event; scrub IDs globally.
function sanitizePath(pathname: string): string {
  return pathname
    .replace(/\/patients\/[^/]+/, "/patients/:patientId")
    .replace(
      /\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}(?=\/|$)/gi,
      "/:id",
    )
    .replace(/\/\d{4,}(?=\/|$)/g, "/:id");
}

function sanitizeUrl(rawUrl: string): string {
  try {
    const url = new URL(rawUrl);
    return `${url.origin}${sanitizePath(url.pathname)}`;
  } catch {
    return "";
  }
}

// Thrown messages interpolate whatever failed, so treat them as untrusted for PHI.
const PHI_TEXT_PATTERNS: [RegExp, string][] = [
  [/[\w.+-]+@[\w-]+\.[\w.-]+/g, ":email"],
  [/\/patients\/[^/\s"']+/g, "/patients/:patientId"],
  [/\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b/gi, ":id"],
  [/\b\d{3}-\d{2}-\d{4}\b/g, ":redacted"],
  [/\b\d{4}-\d{2}-\d{2}\b/g, ":redacted"],
  [/\b\d{1,2}[/-]\d{1,2}[/-]\d{2,4}\b/g, ":redacted"],
  [/\b\d{5,}\b/g, ":redacted"],
];

function sanitizeErrorText(text: string): string {
  return PHI_TEXT_PATTERNS.reduce(
    (scrubbed, [pattern, token]) => scrubbed.replace(pattern, token),
    text,
  );
}

// Stack frames stay untouched: they are bundle paths, and symbolication needs them.
function scrubException(exception: { value?: unknown }): unknown {
  if (typeof exception?.value !== "string") {
    return exception;
  }
  return { ...exception, value: sanitizeErrorText(exception.value) };
}

function scrubEvent(event: CaptureResult | null): CaptureResult | null {
  if (!event) {
    return event;
  }
  const props = event.properties ?? {};
  if (typeof props.$current_url === "string") {
    props.$current_url = sanitizeUrl(props.$current_url);
  }
  if (typeof props.$pathname === "string") {
    props.$pathname = sanitizePath(props.$pathname);
  }
  if (typeof props.$referrer === "string") {
    props.$referrer = sanitizeUrl(props.$referrer);
  }
  if (typeof props.$session_entry_url === "string") {
    props.$session_entry_url = sanitizeUrl(props.$session_entry_url);
  }
  if (typeof props.$exception_message === "string") {
    props.$exception_message = sanitizeErrorText(props.$exception_message);
  }
  if (Array.isArray(props.$exception_list)) {
    props.$exception_list = props.$exception_list.map(scrubException);
  }
  return event;
}

function initializePostHog(): void {
  const backend = getBackend();
  const key = POSTHOG_KEY[backend];
  if (!key) {
    return;
  }
  posthog.init(key, {
    api_host: import.meta.env.VITE_POSTHOG_HOST || "https://us.i.posthog.com",
    defaults: "2026-05-30",
    person_profiles: "identified_only",
    debug: import.meta.env.DEV,
    // Dev only: keep capturing from automated browsers so local smoke tests work.
    opt_out_useragent_filter: import.meta.env.DEV,
    autocapture: false,
    // React boundaries miss event handlers, async work and promise rejections.
    capture_exceptions: true,
    capture_pageview: false,
    capture_pageleave: false,
    before_send: scrubEvent,
    // Recording starts manually once identity confirms a non-internal customer.
    disable_session_recording: true,
    session_recording: {
      maskAllInputs: true,
      maskTextSelector: REPLAY_MASKED_SELECTOR,
      blockSelector: "img, video, canvas",
    },
  });
  initialized = true;
}

function capture(event: string, properties?: Properties): void {
  if (initialized && analyticsEnabled) {
    posthog.capture(event, properties);
  }
}

function captureWithBeacon(event: string, properties?: Properties): void {
  if (initialized && analyticsEnabled) {
    posthog.capture(event, properties, { transport: "sendBeacon" });
  }
}

function captureException(error: unknown, properties?: Properties): void {
  if (initialized && analyticsEnabled) {
    posthog.captureException(error, properties);
  }
}

function startReplay(reason: string, sampleRate: number): void {
  posthog.startSessionRecording({ sampling: true });
  replayRecording = true;
  posthog.register({ replay_reason: reason });
  capture("replay_started", { reason, sample_rate: sampleRate });
}

function boostReplay(reason: string, sampleRate = 1): void {
  if (replayRecording) {
    capture("replay_boosted", { reason, sample_rate: sampleRate });
    return;
  }
  if (replayAllowed === null) {
    pendingReplayBoosts.set(
      reason,
      Math.max(pendingReplayBoosts.get(reason) ?? 0, sampleRate),
    );
    return;
  }
  if (replayAllowed && Math.random() < sampleRate) {
    startReplay(reason, sampleRate);
  }
}

function applyPostHogConfig(
  orgId: string | undefined,
  devModeEnabled: boolean,
  config: PostHogConfig,
): void {
  analyticsEnabled = config.analyticsEnabled;
  replayAllowed =
    initialized &&
    config.replayEnabled &&
    !config.replayExcludedOrgIds.includes(orgId ?? "") &&
    getBackend() === Backend.Production &&
    !isInternalOrg(orgId) &&
    !devModeEnabled;
  if (!replayAllowed) {
    pendingReplayBoosts.clear();
    return;
  }
  pendingReplayBoosts.forEach((sampleRate, reason) => {
    boostReplay(reason, sampleRate);
  });
  pendingReplayBoosts.clear();
  if (!replayRecording && !baselineRolled) {
    baselineRolled = true;
    if (Math.random() < config.replayBaselineSampleRate) {
      startReplay("baseline", config.replayBaselineSampleRate);
    }
  }
}

function identifyUser(
  user: User | undefined,
  organizationName: string,
  practiceCount: number,
): void {
  if (!initialized || !user?.sub) {
    return;
  }
  posthog.identify(user.sub, {
    email: user.email,
    org_id: user.org_id,
    org_name: organizationName,
    is_internal: isInternalOrg(user.org_id),
  });
  if (user.org_id) {
    // practice_count on the org group supports TSO-level rollups.
    posthog.group("organization", user.org_id, {
      name: organizationName,
      practice_count: practiceCount,
    });
  }
}

function registerPracticeContext(
  orgId: string | undefined,
  selectedPracticeIds: string[],
  selectedPracticeNames: string[],
  devModeEnabled: boolean,
): void {
  if (!initialized) {
    return;
  }
  posthog.register({
    org_id: orgId,
    selected_practice_count: selectedPracticeIds.length,
    selected_practice_ids: selectedPracticeIds.slice(0, MAX_SUPER_PRACTICE_IDS),
    is_internal: isInternalOrg(orgId),
    env: ENV_NAME[getBackend()],
    devmode: devModeEnabled,
  });
  // Practice-level attribution is only honest when exactly one practice is selected.
  posthog.resetGroups();
  if (orgId) {
    posthog.group("organization", orgId);
  }
  if (selectedPracticeIds.length === 1) {
    posthog.group("practice", selectedPracticeIds[0], {
      name: selectedPracticeNames[0],
    });
  }
}

export type { PostHogConfig };
export {
  applyPostHogConfig,
  boostReplay,
  capture,
  captureException,
  captureWithBeacon,
  identifyUser,
  initializePostHog,
  isInternalOrg,
  REPLAY_MASKED_SELECTOR,
  registerPracticeContext,
  sanitizePath,
  scrubEvent,
};
